/**
 * @file useCabinetTheme.ts
 * @description Тема кабинета на время жизни каркаса (phase19.29; phase19.31 —
 * палитра из данных, SC1–SC3, SC10).
 *
 * - `useCabinetTheme("commora", заголовок?)` — кабинет платформы и страницы
 *   вне кабинета (вход, выбор кабинета): всегда палитра «Commora» из пакета,
 *   без запроса (SC10). Заголовок вкладки (если передан) ставится на время
 *   жизни и возвращается к заданному в index.html.
 * - `useShopCabinetTheme(shop)` — кабинет магазина: палитра темы магазина.
 *   Свежий кэш ставится сразу (до отрисовки кадра), иначе палитра
 *   запрашивается у сервера и кэшируется; ошибка — «Commora».
 *
 * Палитра ставится на <html> (`lib/cabinet-palette`): диалоги, выпадающие
 * списки и тосты рисуются в порталах и должны получить те же цвета.
 * `useLayoutEffect` — чтобы первый кадр раздела уже был в своей палитре.
 * Снимать палитру при размонтировании не нужно: следующий каркас ставит свою.
 */

import { useLayoutEffect } from "react";

import type { ShopRef } from "../auth/types";
import { getCabinetThemePalette } from "../lib/api-cabinet-themes";
import {
  applyCommoraPalette,
  applyPalette,
  isCacheFresh,
  readCachedPalette,
  resolveShopPalette,
} from "../lib/cabinet-palette";

/** Тема кабинета вне магазина — запись в реестре, а не ветка в коде. */
export type CabinetTheme = "commora";

/**
 * Заголовок вкладки из index.html — запоминаем при загрузке модуля, до любых
 * правок страницами. К нему возвращаемся при выходе из раздела.
 */
const DEFAULT_TITLE = typeof document === "undefined" ? "" : document.title;

/** Палитра «Commora» и (по желанию) заголовок вкладки на время жизни компонента. */
export function useCabinetTheme(theme: CabinetTheme, title?: string): void {
  useLayoutEffect(() => {
    // Реестр тем вне магазина сейчас из одной записи — «Commora».
    if (theme === "commora") applyCommoraPalette();
    if (title !== undefined) document.title = title;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [theme, title]);
}

/** Палитра темы магазина (кэш → сервер → запасная «Commora»). */
export function useShopCabinetTheme(shop: ShopRef | null): void {
  const slug = shop?.slug ?? null;
  const themeId = shop?.cabinetTheme?.id ?? null;
  const updatedAt = shop?.cabinetTheme?.updatedAt ?? null;
  const scheme = shop?.cabinetTheme?.scheme ?? "LIGHT";
  const key = shop?.cabinetTheme?.key ?? "";

  useLayoutEffect(() => {
    if (!slug || !themeId || !updatedAt) {
      // Сервер не прислал тему (старый ответ) — запасная «Commora».
      applyCommoraPalette();
      return;
    }
    const ref = { id: themeId, key, scheme, updatedAt };
    const cached = readCachedPalette(slug);
    if (isCacheFresh(cached, ref)) {
      applyPalette(cached);
      return;
    }
    let alive = true;
    void resolveShopPalette(slug, ref, getCabinetThemePalette).then((palette) => {
      if (alive) applyPalette(palette);
    });
    return () => {
      alive = false;
    };
  }, [slug, themeId, updatedAt, scheme, key]);
}
